import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";
import { api, getCookieConsent } from "@/lib/api";

const GREETING = "Ahoj! Som Ema, AI asistentka Zateplenia.eu. Opýtajte sa na cenu, materiály, normy či dotácie — alebo si hneď vypočítajte cenu zateplenia do 60 sekúnd.";
const CHIPS = [
  { key: "kalkulacka", label: "Vypočítať cenu", href: "/#kalkulacka" },
  { key: "vizualizacia", label: "AI vizualizácia zadarmo", href: "/vizualizacia" },
  { key: "ponuka", label: "Chcem cenovú ponuku", href: "/objednavka" },
  { key: "normy", label: "Normy a hrúbky", href: "/normy" },
];

// Zvýrazni odkazy v odpovedi Emy: markdown [text](url), whitelist interných ciest, strip ** a # značiek
const INTERNAL_PATHS = ["vizualizacia", "galeria", "normy", "objednavka", "kontakt", "blog", "faq", "materialy", "omietky", "realizatori", "partneri"];
const stripMd = (s) => s.replace(/\*\*([^*]+)\*\*/g, "$1").replace(/\*([^*\n]+)\*/g, "$1");
const renderWithLinks = (text) => {
  const mdLinks = [];
  let t = text.replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (_, label, url) => {
    mdLinks.push({ label: stripMd(label), url });
    return `\u0000${mdLinks.length - 1}\u0000`;
  });
  t = stripMd(t).replace(/^#+\s*/gm, "");
  const pathRe = new RegExp(`(\\/(?:#kalkulacka|(?:${INTERNAL_PATHS.join("|")})(?:\\/[a-z0-9\\-]+)?)|https?:\\/\\/[^\\s)]+|\\u0000\\d+\\u0000)`, "gi");
  return t.split(pathRe).map((p, i) => {
    const md = p.match(/^\u0000(\d+)\u0000$/);
    if (md) {
      const { label, url } = mdLinks[Number(md[1])];
      return url.startsWith("/")
        ? <Link key={i} to={url} className="text-gold underline">{label}</Link>
        : <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="text-gold underline break-all">{label}</a>;
    }
    if (/^https?:\/\//i.test(p)) {
      return <a key={i} href={p} target="_blank" rel="noopener noreferrer" className="text-gold underline break-all">{p}</a>;
    }
    if (/^\//.test(p)) {
      return <Link key={i} to={p} className="text-gold underline">{p}</Link>;
    }
    return p;
  });
};

export default function ChatbotWidget() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const sessionRef = useRef("");
  const listRef = useRef(null);
  const [bottomPad, setBottomPad] = useState(20);

  // Cookie banner (z-60, na mobile vysoký) by prekrýval bublinu — dynamicky sa nad neho zdvihneme
  useEffect(() => {
    const update = () => {
      if (getCookieConsent()) { setBottomPad(20); return; }
      const b = document.querySelector('[data-testid="cookie-banner"]');
      setBottomPad(b ? Math.round(b.getBoundingClientRect().height) + 12 : 100);
    };
    update();
    const t = setInterval(update, 1200);
    window.addEventListener("resize", update);
    return () => { clearInterval(t); window.removeEventListener("resize", update); };
  }, []);

  useEffect(() => {
    let sid = localStorage.getItem("z_ema_session");
    if (!sid) {
      sid = (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);
      localStorage.setItem("z_ema_session", sid);
    }
    sessionRef.current = sid;
  }, []);

  useEffect(() => {
    if (open) setMessages((m) => (m.length === 0 ? [{ role: "assistant", content: GREETING }] : m));
  }, [open]);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, busy]);

  if (location.pathname.startsWith("/admin") || location.pathname.startsWith("/firma-portal")) return null;

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setBusy(true);
    try {
      const { data } = await api.post("/chatbot/message", { session_id: sessionRef.current, message: msg });
      if (data.session_id) sessionRef.current = data.session_id;
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: "Prepáčte, Ema je momentálne nedostupná. Zavolajte nám prosím na 0907 649 964 alebo píšte na info@zateplenia.eu." }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.25 }}
            style={{ bottom: bottomPad + 76 }}
            className="fixed right-4 md:right-6 z-40 w-[calc(100vw-2rem)] max-w-[380px] h-[520px] max-h-[70vh] bg-ink-2 border border-gold/30 shadow-2xl shadow-black/60 flex flex-col overflow-hidden"
            data-testid="ema-panel">
            <div className="flex items-center gap-3 px-4 py-3 border-b border-white/10 bg-ink">
              <span className="w-9 h-9 bg-gold text-ink flex items-center justify-center font-display text-lg shrink-0">E</span>
              <div className="min-w-0">
                <p className="text-white font-semibold text-sm leading-tight">Ema · AI asistentka</p>
                <p className="text-white/40 text-xs flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-green-400 inline-block" /> online · odpovedá do pár sekúnd</p>
              </div>
              <button onClick={() => setOpen(false)} data-testid="ema-close" aria-label="Zavrieť chat"
                className="ml-auto text-white/50 hover:text-white p-1"><X className="w-5 h-5" /></button>
            </div>

            <div ref={listRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="ema-messages">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-line ${
                    m.role === "user" ? "bg-gold text-ink font-medium" : "bg-white/5 text-white/85 border border-white/10"}`}>
                    {m.role === "assistant" ? renderWithLinks(m.content) : m.content}
                  </div>
                </div>
              ))}
              {busy && (
                <div className="flex justify-start">
                  <div className="bg-white/5 border border-white/10 px-4 py-3 flex gap-1.5" data-testid="ema-typing">
                    {[0, 1, 2].map((d) => (
                      <span key={d} className="w-1.5 h-1.5 rounded-full bg-gold animate-bounce" style={{ animationDelay: `${d * 0.15}s` }} />
                    ))}
                  </div>
                </div>
              )}
            </div>

            {messages.length <= 1 && (
              <div className="px-4 pb-2 flex flex-wrap gap-2" data-testid="ema-chips">
                {CHIPS.map((c) => (
                  <Link key={c.key} to={c.href} onClick={() => setOpen(false)} data-testid={`ema-chip-${c.key}`}
                    className="px-3 py-1.5 text-xs border border-gold/40 text-gold hover:bg-gold hover:text-ink transition-colors">
                    {c.label}
                  </Link>
                ))}
              </div>
            )}

            <div className="p-3 border-t border-white/10 flex gap-2">
              <input
                value={input} onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Napíšte otázku…" data-testid="ema-input" maxLength={1000}
                className="flex-1 bg-ink border border-white/15 px-3.5 py-2.5 text-white text-sm placeholder:text-white/30 focus:border-gold outline-none" />
              <button onClick={() => send()} disabled={busy || !input.trim()} data-testid="ema-send" aria-label="Odoslať"
                className="bg-gold text-ink px-3.5 hover:bg-gold-light transition-colors disabled:opacity-40">
                <Send className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        whileHover={{ scale: 1.06 }} whileTap={{ scale: 0.94 }}
        onClick={() => setOpen((o) => !o)} data-testid="ema-bubble" aria-label="Otvoriť chat s asistentkou Emou"
        style={{ bottom: bottomPad }}
        className="fixed right-4 md:right-6 z-40 w-14 h-14 rounded-full bg-gold text-ink shadow-lg shadow-gold/25 flex items-center justify-center transition-[bottom] duration-300">
        {open ? <X className="w-6 h-6" /> : <Sparkles className="w-6 h-6" />}
      </motion.button>
    </>
  );
}
